import { use, useContext, useEffect, useMemo, useState } from "react";
import { GoBackButton, IconButton, StandardButton } from "../atoms/buttons";
import { TextAtom } from "../atoms/TextAtom";
import { TitleAtom } from "../atoms/titleAtom";
import { GridLayout, HorizontalLayout, VerticalLayout, VerticalPageLayout } from "../components/layout";
import { PageContext, ToastContext } from "../App";
import { deleteAPI, getAPI, postAPI, updateAPI } from "../api/api";
import { Spinner } from "../atoms/spinner";
import { ReusableTable } from "../components/ReusableTableComp";
import { LABEL_AI, STATUS, STATUS_AI } from "../constants/statusLabels";
import { IconBorderBottom } from "@tabler/icons-react";
import { icon } from "../functions/icons";
import { border, borderColor, margin, padding, position } from "polished";
import { AddTaskToCompany, AddTaskToLead } from "../components/taskModal";
import { he, id, is } from "date-fns/locale";
import { LeadComp, LeadRow } from "../components/leadComp";
import { m } from "framer-motion";
import { PagePagination } from "../molecule/PagePagination";
import { InputForm, TextAreaForm } from "../atoms/input";
import { StandardComp } from "../atoms/standardComp";
import { min, set } from "date-fns";
import { Chip } from "../molecule/Chip";
import { Divider } from "../atoms/divider";
import { SuggestedCourseComp } from "../components/suggestCourseComp";
import { isDisabled } from "@testing-library/user-event/dist/utils";
import { Title } from "chart.js";
import { IconAtom } from "../atoms/iconAtom";

import { Modal, ModalBody, ModalFooter, ModalTitle } from "../components/modal";
import { ActivitiesComp, LeadActivities, RecentActivities } from "../components/recentActivities";
import { GoalDoughnutChart, GraphComp } from "../components/graphComp";
import { LinkedinComp } from "../components/linkedinComp";


export function TasksLeadInfo({ oldPage, pageSwap }) {
    const navigationContext = useContext(PageContext);
    const { id_lead, id_lead_company } = navigationContext.sidebar;
    const entityType = id_lead_company ? "company" : "lead";
    const entityId = id_lead_company || id_lead;
    const [leadData, setLeadData] = useState({});
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [companyLeads, setCompanyLeads] = useState([]);
    const [companyNotes, setCompanyNotes] = useState([]);
    const [leadNotes, setLeadNotes] = useState([]);
    const [insights, setCompanyInsights] = useState({});
    const resetState = () => {
        setLeadData({});
        setTasks([]);
        setCompanyLeads([]);
        setCompanyNotes([]);
        setLeadNotes([]);
    };
    const loadLeads = async () => {
        setLoading(true);
        if (entityType === "company") {
            await loadCompanyData(entityId);
            setLoading(false);
            return;
        }
        const query = `api/leads/${id_lead}`;
        const res = await getAPI(query);
        if (res.status === "ok") {
            setLeadData(res.data.lead);
            setLeadNotes(res.data.notes || []);
            setTasks(res.data.tasks || []);
        }
        setLoading(false);

    }
    const loadCompanyData = async (id_company) => {
        setLoading(true);
        const query = `api/leads/company/${id_company}`;
        const res = await getAPI(query);
        if (res.status === "ok") {
            setLeadData(res.data.company?.[0]);
            setCompanyLeads(res.data.leads || []);
            setCompanyNotes(res.data.notes || []);
            setCompanyInsights(res.data.insights || [])
            setTasks(res.data.tasks || []);
        }
        setLoading(false);
    }
    const handleDeepSearch = async () => {
        setLoading(true);
        const query = "api/leads/companies/deep-search"
        const body = { "id_lead_company": id_lead_company }
        await postAPI(query, body)
        navigationContext.handleNavigation({ ...navigationContext.sidebar, info_complete: 1 })
        setLoading(false)
    }

    const completeInfo = async () => {
        if (id_lead) { await handleDeepSearchLead() } else { await handleDeepSearch() }

        resetState()
        await loadLeads()
    }

    const handleDeepSearchLead = async () => {
        setLoading(true)
        const query = "api/leads/lead/deep-search"
        const body = { "id_lead": id_lead }
        const load = await postAPI(query, body)
        setLoading(false)
    }
    useEffect(() => {

        if (navigationContext.sidebar.info_complete == 0) {
            completeInfo()
            return
        }
        resetState();
        loadLeads()
    }, [id_lead, id_lead_company, navigationContext.sidebar.navigation])

    const leadName = entityType === "company" ? leadData?.name || "Onbekend bedrijf" : `${leadData?.first_name || ""} ${leadData?.last_name || ""}`.trim() || leadData?.email || "Onbekende lead";
    const handleGoBack = () => {
        if (!navigationContext.sidebar.id_lead_company && navigationContext.sidebar.old_id_lead_company && navigationContext.sidebar.back_page === 19) {
            return navigationContext.handleNavigation({ ...navigationContext.sidebar, id_lead: null, id_lead_company: navigationContext.sidebar.old_id_lead_company, back_page: 20 });
        }
        navigationContext.handleNavigation({ ...navigationContext.sidebar, navigation: navigationContext.sidebar.back_page });
    }
    const handleGoToLeads = (lead) => {
        navigationContext.handleNavigation({
            ...navigationContext.sidebar,
            navigation: 19,
            id_lead: lead.id_lead,
            id_lead_company: null,
            old_id_lead_company: id_lead_company,
            back_page: 19,
            info_complete: lead.info_complete
        })
    }
    const isMobile = window.innerWidth <= 768;
    if (loading) {
        return <VerticalPageLayout className={"center"}><TextAtom title={`We verzamelen extra informatie over deze ${entityType === "company" ? "organisatie" : "lead"}.`} /><Spinner /></VerticalPageLayout>
    }
    return (
        <VerticalPageLayout className={`standard-padding page-in-animation ${pageSwap && "page-out-animation"}`} style={{ width: "auto", gap: "1.5rem" }}>
            <HorizontalLayout style={{ gap: "1rem" }}>
                <GoBackButton className={"no-margin"} left={true} onClick={handleGoBack} />
                <VerticalLayout>
                    <TitleAtom title={leadName} />
                    <TextAtom className={"standard-text-color-light small-text"} title={entityType === "company" ? leadData?.linkedin_url || "" : leadData?.company_name || leadData?.email} />
                </VerticalLayout>
                <ConvertToClient />
            </HorizontalLayout>
            {isMobile ? (
                <MobileDetailComp
                    key={`${entityType}-${entityId}`}
                    notes={entityType === "company" ? companyNotes : leadNotes}
                    entityType={entityType}
                    lead={leadData}
                    id={entityId}
                    insights={insights}
                    load={loadLeads}
                />
            ) : (
                <DetailsComp
                    key={`${entityType}-${entityId}`}
                    notes={entityType === "company" ? companyNotes : leadNotes}
                    entityType={entityType}
                    lead={leadData}
                    insights={insights}
                    id={entityId}
                    load={loadLeads}
                />
            )}
            <LeadInfoTable lead={leadData} tasks={tasks} load={loadLeads} entityType={entityType} />
            {entityType === "company" && (
                <Leads leads={companyLeads} onClick={handleGoToLeads} load={loadLeads} />
            )}
        </VerticalPageLayout>
    )
}

const Leads = ({ leads = [], onClick, load }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const ITEMS_PER_PAGE = 5;
    const paginatedLeads = useMemo(() => {
        const start = currentPage * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        return leads.slice(start, end);
    }, [leads, currentPage]);

    return (
        <>
            <VerticalLayout
                className="card-background-color lead-row-container"
                style={{
                    padding: ".5rem",
                    width: "auto",
                    borderRadius: "calc(.5rem * var(--br-scale))",
                    border: "1px solid var(--border-color)",
                }}
            >
                <LeadsListHeader load={load} />
                {paginatedLeads.map((lead) => (
                    <HorizontalLayout
                        key={lead.id_lead}
                        className="lead-row"
                        onClick={() => onClick(lead)}
                        style={{ padding: "1rem", gap: ".5rem", width: "auto" }}
                    >
                        <LeadRow lead={lead} />
                    </HorizontalLayout>
                ))}
                <PagePagination
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    totalUnits={leads.length}
                    unitName="leads"
                    itemsPerPage={ITEMS_PER_PAGE}
                />
            </VerticalLayout>
        </>
    );
};
const LeadsListHeader = ({ load }) => {
    const [activeModal, setActiveModal] = useState(false);
    const isMobile = window.innerWidth <= 768;
    return (
        <>
            <AddLeadModal visible={activeModal} dismiss={() => setActiveModal(false)} load={load} />
            <HorizontalLayout style={{ padding: ".5rem", width: "auto" }}>
                <TitleAtom className="medium-text standard-text-color" title="Leads binnen dit bedrijf" />
                <StandardButton style={{ gap: ".5rem" }} onClick={() => setActiveModal(true)}>
                    {icon("plus", 20, "white")}
                    {!isMobile && "Lead toevoegen"}
                </StandardButton>
            </HorizontalLayout>
        </>

    )
}

const AddLeadModal = ({ visible, dismiss, load }) => {
    const navigationContext = useContext(PageContext);
    const { id_lead_company } = navigationContext.sidebar;
    const toastContext = useContext(ToastContext);
    const [createModalData, setCreateModalData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        company_name: "",
        phone_number: "",
        url: null,
    });
    const updateCreatedUser = (value, field) => {
        setCreateModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const addLead = async () => {
        if (!createModalData.first_name || !createModalData.email) {
            return toastContext.showToast("Voornaam en e-mail zijn verplicht.", "error");
        }
        const query = "api/leads/add";
        const body = {
            ...createModalData,
            id_lead_company: id_lead_company
        }
        const res = await postAPI(query, body);
        console.log("add lead response", res)
        if (res.status === "ok") {
            dismiss();
            load();
            toastContext.showToast("Lead succesvol toegevoegd!", "success");
        }
        if (res.message === "Duplicated data") {
            toastContext.showToast("Er bestaat al een lead met dit e-mailadres. Probeer het opnieuw met een ander e-mailadres.", "error");
        }
    }
    const inputFields = [
        { key: "first_name", title: "Voornaam*", placeholder: "John" },
        { key: "last_name", title: "Achternaam", placeholder: "Optioneel" },
        { key: "email", title: "Email*", placeholder: "john.doe@example.com" },
        { key: "phone_number", title: "Telefoonnummer", placeholder: "Optioneel" },
        { key: "url", title: "Url", placeholder: "Website of linkedin" },
    ];
    return (
        <Modal height={null} visible={visible} dismiss={dismiss}>
            <ModalTitle title={"Nieuwe lead toevoegen"} hasXmark={true} dismissable={dismiss} />
            <ModalBody>
                <HorizontalLayout style={{ gap: "1rem" }}>
                    {inputFields.slice(0, 2).map(field => (
                        <InputForm
                            key={field.key}
                            value={createModalData[field.key]}
                            typeInput={(v) => updateCreatedUser(v, field.key)}
                            title={field.title}
                            placeholder={field.placeholder}
                        />
                    ))}
                </HorizontalLayout>

                {inputFields.slice(2).map(field => (
                    <InputForm
                        key={field.key}
                        value={createModalData[field.key]}
                        typeInput={(v) => updateCreatedUser(v, field.key)}
                        title={field.title}
                        placeholder={field.placeholder}
                    />
                ))}
            </ModalBody>
            <ModalFooter>
                <StandardButton title={"Annuleren"} backgroundColor="grey-200" textColor="grey-800" onClick={dismiss} />
                <StandardButton title={"Toevoegen"} onClick={addLead} />
            </ModalFooter>
        </Modal>
    )
}

const ConvertToClient = () => {
    return (
        <StandardButton
            style={{
                borderRadius: "100px",
                boxShadow: "0 5px 5px rgba(0 ,0, 0 ,0.2)",
                borderStyle: "solid",
                borderColor: "white",
                fontWeight: "bold",
                width: "8rem",
                justifyContent: "center"
            }}
            className={'primary-button'}
            title={"Deal"}
        />
    )
}

const MobileDetailComp = ({ lead, entityType, id, notes, load, insights }) => {
    const details = [
        { label: "E-mail", value: lead.email_address, icon: "mail", action: `mailto:${lead.email_address}` },
        { label: "Telefoon", value: lead.phone_number, icon: "phone", action: `tel:${lead.phone_nummer}` },
        { label: "Bedrijf", value: lead.company_name, icon: "company" },
        {
            label: "Laatste activiteit",
            value: lead.last_activity
                ? new Date(lead.last_activity).toLocaleDateString()
                : "Geen activiteit",
            icon: "activity"
        },
    ];

    return (
        <VerticalLayout style={{ gap: "1rem", width: "auto", }}>
            <VerticalLayout
                className="card-background-color"
                style={{
                    padding: ".5rem",
                    borderRadius: "12px",
                    border: "1px solid var(--border-color)",
                    width: "auto",
                }}
            >
                <TitleAtom className="medium-text" title="Lead details" />
                {details.map((item, index) => (
                    <HorizontalLayout
                        key={index}
                        style={{
                            width: "auto",
                            gap: "1rem",
                            alignItems: "center",
                            padding: ".75rem 0",
                            borderRadius: "10px",
                        }}
                        onClick={() => item.action && window.open(item.action)}
                    >
                        {icon(item.icon, 22, "var(--primary-600)")}

                        <VerticalLayout style={{ gap: ".15rem" }}>
                            <TextAtom
                                className="xsmall-text standard-text-color-light"
                                title={item.label}
                            />
                            <TextAtom
                                className="small-text semi-bold standard-text-color"
                                title={item.value || "N/A"}
                            />
                        </VerticalLayout>
                    </HorizontalLayout>
                ))}
            </VerticalLayout>
            <Notes id={id} entityType={entityType} notes={notes} load={load} />
            <SuggestionComp insights={insights} />
            {entityType === "lead" && <VerticalLayout
                className="card-background-color"
                style={{
                    gap: ".5rem",
                    padding: ".5rem",
                    borderRadius: "12px",
                    border: "1px solid var(--border-color)",
                    width: "auto",
                }}
            >
                {entityType === "lead" && <Steps />}
            </VerticalLayout>}
        </VerticalLayout>
    );
};


const DetailsComp = ({ lead, entityType, id, notes, load, insights }) => {

    const details = [
        { label: "E-mail", value: lead.email_address || "N/A", icon: "mail" },
        { label: "Telefoonnummer", value: lead.phone_number || "N/A", icon: "phone" },
        { label: "Bedrijf", value: lead.company_name || "N/A", icon: "company" },
        { label: "Linkedin url", value: lead.url || "N/A", icon: "linkedin" },
        { label: "Laatste activiteit", value: lead.last_activity ? new Date(lead.last_activity).toLocaleDateString() : "N/A", icon: "activity" },
    ];
    
    return (
        <>
            <HorizontalLayout style={{ gap: ".5rem", alignItems: "flex-start" }}>
                <VerticalLayout style={{ gap: ".5rem" }}>
                    <DetailGrid details={details} />
                    {entityType == "company" && <SuggestionComp insights={insights} />}
                    {entityType == "lead" &&
                        <>

                            <LeadScore />
                            <LeadActivities />
                            <LinkedinComp />
                        </>
                    }
                    {/* <TasksSuggestions /> */}
                    <CourseSuggestions />
                </VerticalLayout>
                <VerticalLayout style={{ width: "unset", gap: ".5rem" }}>
                    {entityType === "lead" && <Steps />}
                    <Notes id={id} entityType={entityType} notes={notes} load={load} />
                </VerticalLayout>
            </HorizontalLayout>
        </>
    )
}


const LeadScore = () => {
    const navigationContext = useContext(PageContext);
    const { id_lead } = navigationContext.sidebar;
    const [factors, setFactors] = useState({ description: "Heeft al vaker iets gedaan hierin" })
    const [lead_score, setLeadScore] = useState(0)
    useEffect(() => {
        load_score()
    }, [])

    const load_score = async () => {

        const query = `api/leads/${id_lead}/score`
        const load = await getAPI(query)
        setFactors(load.data.factors)
        setLeadScore(load.data.score)
    }
    return (
        <HorizontalLayout style={{ width: "unset", gap: "1rem" }}>
            <LeadScoreGraph currentValue={Math.floor(lead_score)} />
            <LeadScoreFactors
                factors={factors}
            />
        </HorizontalLayout>
    )
}

const LeadScoreGraph = ({ currentValue }) => {
    return (
        <StandardComp style={{ width: "100%", margin: 0, height: "calc(100% - 3rem)" }}>
            <TitleAtom

                title={"Lead score"}
            />
            <GoalDoughnutChart
                currentValue={currentValue}
                graph={"goal"}
            />
        </StandardComp>
    )
}
const LeadScoreFactors = ({ factors }) => {
    console.log(Object.keys(factors))
    return (
        <StandardComp style={{ width: "100%", margin: 0, height: "100%", padding: 0 }}>
            <TitleAtom
                style={{ padding: "1rem", paddingTop: "1.3rem" }}
                title={"Lead score factoren"}
            />
            {Object.keys(factors).map((key) => {
                return (
                    (factors[key].passed == true) && <ScoreFactor
                        factor={factors[key]}
                    />
                )
            })}
        </StandardComp>
    )
}
const ScoreFactor = ({ factor }) => {
    return (

        <HorizontalLayout style={{ gap: "0.5rem", margin: "1rem" }}>
            <div class="bullet-point" style={{ backgroundColor: factor.passed && "var(--success-500)" }}></div>
            <TextAtom
                title={factor.title_nl}
            />
        </HorizontalLayout>

    )
}
const SuggestionComp = ({ insights }) => {
    const [suggestionData, setSuggestionData] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [selectedActions, setSelectedActions] = useState([]);
    const isMobile = window.innerWidth <= 768;
    const navigationContext = useContext(PageContext);
    const { id_lead, id_lead_company } = navigationContext.sidebar;
    const leadStatus = 4;
    const dummyData = {
        reason: "Deze lead is kansrijk omdat ze recent interesse hebben getoond in e-learning en actief zijn op LinkedIn.",
        learnings: [
            { icon: "wand", text: "Lead heeft interesse in persoonlijke ontwikkeling." },
            { icon: "users", text: "Ze zoeken naar management training voor hun team." },
            { icon: "clock", text: "De lead heeft een drukke agenda, dus korte modules werken beter." },
        ],
        actions: [
            { id_action: 1, title: "Stuur voorstel", description: "Maak een gepersonaliseerd voorstel met 3 opties." },
            { id_action: 2, title: "Bel lead", description: "Vraag naar hun huidige uitdaging en tijdsplanning." },
            { id_action: 3, title: "Stuur e-learning preview", description: "Laat 1 module zien als voorbeeld." }
        ],
        recommended_courses: [
            { id_course: 10, title: "Time management", image_url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80" },
            { id_course: 12, title: "Team leadership", image_url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80" },
            { id_course: 14, title: "Effectief communiceren", image_url: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=800&q=80" }
        ]
    };

    // useEffect(() => {
    //     loadInsights()
    // }, []);


    // const loadInsights = async () => {
    //     const query = `api/leads/company/${id_lead_company}/insights`
    //     const insights = await getAPI(query)
    //     setSuggestionData(insights.data)

    // }

    const handleDeepSearch = async () => {
        setAiLoading(true);
        const query = "api/leads/companies/deep-search"
        const body = { "id_lead_company": id_lead_company }
        await postAPI(query, body)
        setAiLoading(false)
    }
    const courses = suggestionData?.recommended_courses || [];
    const isDisabled = selectedActions.length === 0 && selectedCourses.length === 0;
    return (
        <VerticalLayout
            className="lead-detail-box"
            style={{
                width: "unset",
                gap: "1rem",
                height: "max-content",
            }}
        >
            <HorizontalLayout style={{ gap: "1rem" }}>
                {icon("ai-suggestion", 24, "var(--primary-600)")}
                {!isMobile && <TitleAtom
                    className={"medium-text"}
                    style={{ width: "unset", color: "var(--primary-600)" }}
                    title={"Diepgaande inzichten "}
                />}

                {true ? <DeepSearchButton handleDeepSearch={handleDeepSearch} /> : <Chip
                    style={{ marginLeft: "auto" }}
                    label={LABEL_AI[leadStatus]}
                    variant={STATUS_AI[leadStatus]}
                />}

            </HorizontalLayout>

            <Divider color="var(--divider-color)" />
            <VerticalLayout style={{ width: "auto", gap: ".5rem" }}>
                <IconButton className="refresh-button" color="primary-600" style={{ position: "absolute", right: "0", top: "0" }} icon2="refresh" onClick={() => { }} />
                {aiLoading && (
                    <TextAtom
                        className="small-text standard-text-color-light"
                        title="AI suggesties laden..."
                    />
                )}
                {insights?.core_business && (
                    <>
                        <TitleAtom

                            className={"small-text"}
                            style={{ width: "unset", }}
                            title={"Wat is de core business van deze organisatie?"}
                        />
                        <TextAtom
                            className="small-text standard-text-color"
                            title={insights["core_business"].text}
                        />
                    </>
                )}
                {Object.keys(insights).length > 0 && (
                    <>
                        <TitleAtom
                            className={"small-text"}
                            style={{ width: "unset", }}
                            title={"Belangrijkste inzichten"}
                        />
                        <ul style={{ padding: "0", margin: "0", listStyleType: "none" }}>
                            {Object.keys(insights).map((item, idx) => (
                                (item != "core_business") &&
                                <li key={idx} style={{ gap: "1rem", display: "flex", alignItems: "center" }}>
                                    {icon(insights[item].icon || "info", 14, "var(--primary-600)")}
                                    <VerticalLayout>
                                        <TextAtom className="small-text standard-text-color bold-text" title={insights[item].title} />
                                        <TextAtom className="small-text standard-text-color" title={insights[item].text} />
                                    </VerticalLayout>

                                </li>
                            ))}
                        </ul>
                    </>
                )}

            </VerticalLayout>


        </VerticalLayout>
    );
};

const CourseSuggestions = () => {
    const navigationContext = useContext(PageContext);
    const { id_lead_company } = navigationContext.sidebar;
    const isMobile = window.innerWidth <= 768;
    const [aiLoading, setAiLoading] = useState(false)
    const [courses, setCourses] = useState([

    ])
    const [selectedCourses, setSelectedCourses] = useState([])
    useEffect(() => {
        loadSuggestedCourses()
    }, [])

    const loadSuggestedCourses = async () => {
        const query = `api/leads/company/${id_lead_company}/course-suggestions`
        const data = await getAPI(query)
        console.log(data)
        setCourses(data.data)
    }
    const createCourseSuggestions = async () => {
        setAiLoading(true)
        setCourses([])
        const body = { id_lead_company: id_lead_company }

        const res = await postAPI("api/leads/company/create-suggestions", body);
        if (res.status === "ok") {
            setCourses(res.data);
        }
        setAiLoading(false)
    };

    const handleCourseToggle = (courseId) => {
        setSelectedCourses((prevSelected) => {
            if (prevSelected.includes(courseId)) {
                return prevSelected.filter((id) => id !== courseId);
            }
            return [...prevSelected, courseId];
        });
    };

    const createCourse = async (course, index) => {
        const copy_courses = [...courses]
        copy_courses[index].ai_loading = true
        setCourses(copy_courses)
        const query = 'api/courses/create'
        const body = { ...course }
        body.course_template = 2
        body.lesson_count = 2
        body.description = body.long_description
        body.level = 3
        body.research_mode = "medium"
        body.price = 0
        body.exam = false
        body.ai_instructions = ""
        body.allow_converter = false
        body.allow_file_upload = false
        body.structure_mode = "automatic"
        body.pdf_files = []
        body.documents = []
        body.text_transcription = false
        body.ai_mode = 1

        await postAPI(query, body)


    }
    return (
        <VerticalLayout className="lead-detail-box"
            style={{
                width: "unset",
                gap: "1rem",
                height: "max-content",
            }}
        >
            <HorizontalLayout style={{ gap: "1rem" }}>
                {icon("book", 24, "var(--primary-600)")}
                {!isMobile && <TitleAtom
                    className={"medium-text"}
                    style={{ width: "unset", color: "var(--primary-600)" }}
                    title={"Learning aanbevelingen"}
                />}

                <DeepSearchButton handleDeepSearch={createCourseSuggestions} />

            </HorizontalLayout>

            {courses.length > 0 && <Divider color="var(--divider-color)" />}
            {courses?.length > 0 && (
                <>
                    {/* <Divider color="var(--divider-color)" /> */}


                    {courses.map((course, index) => (

                        <SuggestedLeadCourses
                            aiLoading={course.ai_loading}
                            key={course.id_course}
                            title={course.title}
                            description={course.short_description}
                            image={course.image_url}
                            select={() => handleCourseToggle(course.id_course)}
                            selected={selectedCourses.includes(course.id_course)}
                            generate={() => createCourse(course, index)}
                        />

                    ))}

                </>
            )
            }
            {aiLoading === true && <Spinner style={{ marginLeft: "auto", marginRight: "auto", marginTop: "1rem", marginBottom: "1rem" }} />}

        </VerticalLayout>
    )
}

const SuggestedLeadCourses = ({ title, description, label, selected, select, aiLoading, generate }) => {
    return (
        <HorizontalLayout onClick={select} className="standard-comp" style={{ width: "unset", borderColor: selected && "var(--primary-500)", transition: "0.3s" }} >
            <VerticalLayout>
                <HorizontalLayout>
                    <TitleAtom className="small-text" title={title} />
                    <Chip
                        title={label}
                    />

                </HorizontalLayout>
                {!aiLoading && <TextAtom className="small-text" title={description} />}

            </VerticalLayout>
            <StandardButton
                onClick={generate}
                title={"Genereer"}
            />
            {
                aiLoading &&
                <VerticalLayout style={{ position: "absolute", justifyContent: "center", alignItems: "center", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(255,255,255,0.6)", borderRadius: "12px" }}>

                    <Spinner size={22} />
                    <TextAtom className="xsmall-text" title="learning aan het genereren... houd de learning pagina in de gaten " />
                </VerticalLayout>
            }
        </HorizontalLayout>

    )
}

const TasksSuggestions = () => {
    const isMobile = window.innerWidth <= 768;
    const [selectedActions, setSelectedActions] = useState([]);
    const [suggestionData, setSuggestionData] = useState([
        { id_action: 1, title: "Stuur voorstel", description: "Maak een gepersonaliseerd voorstel met 3 opties." },
        { id_action: 2, title: "Bel lead", description: "Vraag naar hun huidige uitdaging en tijdsplanning." },
        { id_action: 3, title: "Stuur e-learning preview", description: "Laat 1 module zien als voorbeeld." }
    ]);
    const handleActionToggle = (actionId) => {
        setSelectedActions((prevSelected) => {
            if (prevSelected.includes(actionId)) {
                return prevSelected.filter((id) => id !== actionId);
            }
            return [...prevSelected, actionId];
        });
    }

    const loadSuggestedTasks = () => {
        return (
            console.log("Load suggested tasks")
        )
    }
    return (
        <VerticalLayout className="lead-detail-box"
            style={{
                width: "unset",
                gap: "1rem",
                height: "max-content",
            }}
        >

            <HorizontalLayout style={{ gap: "1rem" }}>
                {icon("activity", 24, "var(--primary-600)")}
                {!isMobile && <TitleAtom
                    className={"medium-text"}
                    style={{ width: "unset", color: "var(--primary-600)" }}
                    title={"Aanbevolen acties & taken"}
                />}

                <DeepSearchButton handleDeepSearch={loadSuggestedTasks} />
            </HorizontalLayout>
            {suggestionData?.length > 0 && <Divider />}
            {suggestionData?.length > 0 && (
                <>

                    <GridLayout
                        gap="1rem"
                        columnsTemplate="repeat(auto-fit, minmax(200px, 1fr))"
                        rowTemplate={"auto"}
                        style={{ marginTop: ".5rem", width: "unset" }}
                    >
                        {suggestionData.map((action, idx) => (
                            <VerticalLayout
                                key={idx}
                                onClick={() => handleActionToggle(action.id_action)}
                                className={`card-background-color action-suggest ${selectedActions.includes(action.id_action) ? "selected" : ""}`}
                                style={{
                                    padding: ".75rem",
                                    borderRadius: "12px",
                                    border: "1px solid var(--border-color)",
                                    gap: ".5rem",
                                    width: "auto",
                                    height: "auto",
                                }}
                            >
                                <TextAtom className="small-text semi-bold standard-text-color" title={action.title} />
                                <TextAtom className="xsmall-text standard-text-color-light" title={action.description} />
                            </VerticalLayout>
                        ))}
                    </GridLayout>
                </>
            )}


        </VerticalLayout>
    )
}


const DetailGrid = ({ details, load }) => {
    const isMobile = window.innerWidth <= 768;
    const [editModal, setEditModal] = useState(false);
    const pageContext = useContext(PageContext)
    const { id_lead } = pageContext.sidebar
    return (
        <>
            <EditModal visible={editModal} dismiss={() => setEditModal(false)} info={details} load={load} />
            <VerticalLayout className="lead-detail-box" style={{ gap: ".5rem", height: "auto", width: "unset" }}>
                <HorizontalLayout className="space-between">
                    <TitleAtom className={"medium-text"} style={{ width: "unset", }} title={"Lead Details"} />
                    <IconButton onClick={() => setEditModal(!editModal)} icon2={"edit"} style={{ color: "var(--primary-600)", }} />
                </HorizontalLayout>
                <GridLayout rowTemplate="repeat(2, 1fr)" columnsTemplate={"repeat(2, auto)"} style={{ height: "100%", paddingLeft: ".5rem", width: "100%", alignItems: "center" }}>
                    {details.map((item, index) => (
                        <HorizontalLayout style={{ gap: "1rem", height: "max-content" }}>
                            {icon(item.icon, 24, "var(--primary-600)")}
                            <VerticalLayout>
                                <TitleAtom style={{ width: "auto" }} className={"small-text"} key={index} title={item.label} />
                                <TextAtom
                                    key={index}
                                    className="small-text standard-text-color-light"
                                    title={item.value}
                                />
                            </VerticalLayout>
                        </HorizontalLayout>

                    ))}
                    {id_lead && <LinkedinConnection />}
                </GridLayout>

            </VerticalLayout>
        </>
    )
}

const LinkedinConnection = () => {
    const [activeConnection, setActiveConnection] = useState(false)
    const [connected, setConnected] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const page = useContext(PageContext)
    const { id_lead } = page.sidebar
    const createConnection = async () => {
        setActiveConnection(true)
        setIsLoading(true)
        const query = "api/linkedin/make-connection"
        const body = { id_lead: id_lead }
        const create = await postAPI(query, body)
        if (create.status == "ok") {
            setActiveConnection(true)
        }
        setActiveConnection(true)
        setIsLoading(false)
    }

    const loadConnection = async () => {
        const query = `api/linkedin/status?id_lead=${id_lead}`
        const status = await getAPI(query)
        if (status.status == "ok") {
            
            setConnected(status.data.connected)
        }
    }
    useEffect(() => {
        loadConnection();
    }, [id_lead]);
    return (


        <HorizontalLayout style={{ gap: "1rem" }}>
            <IconAtom
                style={{ padding: 0 }}
                iconSize={24}
                color={"var(--primary-500)"}
                icon2={"linkedin"}
            />
            <VerticalLayout style={{ width: "unset" }}>
                <TitleAtom style={{ width: "auto" }} className={"small-text"} title={"Linkedin"} />
                <ConnectionStatus connected={connected} isLoading={isLoading} createConnection={() => createConnection()} />
            </VerticalLayout>
        </HorizontalLayout>
    )
}


const ConnectionStatus = ({ connected, isLoading, createConnection }) => {
    console.log(connected, "linkedin connection status")
    return (
        <>
            {connected == 3 &&
                <HorizontalLayout style={{ gap: "0.5rem" }}>
                    <div className="bullet-point" style={{ backgroundColor: "var(--success-600)" }}></div>
                    <TextAtom
                        title={"Is connectie"}
                    />

                </HorizontalLayout>
            } 
            {connected == 2 &&
                <HorizontalLayout style={{ gap: "0.5rem" }}>
                    <div className="bullet-point" style={{ backgroundColor: "var(--warning-600)" }}></div>
                    <TextAtom
                        title={"Connectie verzoek verzonden"}
                    />

                </HorizontalLayout>
            }
            {
                connected == 1 &&
                <HorizontalLayout style={{ gap: "0.5rem" }}>
                    <div className="bullet-point" style={{ backgroundColor: "var(--primary-600)" }}></div>
                    <TextAtom
                        title={"Linkedin account gevonden"}
                    />

                </HorizontalLayout>
            }
            {
                (connected == 0) && <LinkedinConnctButton createConnection={createConnection} isLoading={isLoading} />
            }
            </>
    )
}

const LinkedinConnctButton = ({ createConnection, isLoading }) => {
    return (
        !isLoading ? <StandardButton
            onClick={createConnection}
            style={{ margin: 0 }}
            title={"Maak connectie"}
        />
            :
            <HorizontalLayout style={{ gap: "0.5rem" }}>
                <Spinner size="28px" />
                <TextAtom style={{ fontSize: "0.7rem", fontWeight: "bold" }} title={"Connectie verzoek sturen..."} />
            </HorizontalLayout>
    )
}   
const EditModal = ({ visible, dismiss, info, load }) => {
    const [createModalData, setCreateModalData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        linkedin_url: null,
    });

    const updateCreatedUser = (value, field) => {
        setCreateModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const inputFields = [
        { key: "first_name", title: "Voornaam*", placeholder: "Jan" },
        { key: "last_name", title: "Achternaam", placeholder: "Jansen" },
        { key: "email", title: "Email*", placeholder: "Optioneel" },
        { key: "linkedin_url", title: "LinkedIn URL", placeholder: "Optioneel" },
        { key: "phone_number", title: "Telefoonnummer", placeholder: "Optioneel" },
    ];
    useEffect(() => {
        setCreateModalData(
            info.reduce((acc, item) => {
                acc[item.key] = item.value;
                return acc;
            }, {})
        )
    }, [])
    return (
        <Modal height={null} visible={visible} dismiss={dismiss}>
            <ModalTitle title={"Lead details bewerken"} hasXmark={true} dismissable={dismiss} />
            <ModalBody>
                {inputFields.map(field => (
                    <InputForm
                        key={field.key}
                        value={createModalData[field.key]}
                        typeInput={(v) => updateCreatedUser(v, field.key)}
                        title={field.title}
                        placeholder={field.placeholder}
                    />
                ))}
            </ModalBody>
        </Modal>
    )
}

const Notes = ({ notes, id, entityType, load }) => {
    const [noteText, setNoteText] = useState("");
    const [editedNotes, setEditedNotes] = useState({});
    const toast = useContext(ToastContext)
    const handleSave = async () => {
        if (noteText.trim() === "") { return toast.showToast("Notitie kan niet leeg zijn", "error", 3000); }
        const query = (entityType === "company") ? "api/tasks/add/note/company" : "api/tasks/add/note/lead";
        const keyToSave = (entityType === "company") ? "id_lead_company" : "id_lead";
        const body = { note: noteText, [keyToSave]: id };
        const res = await postAPI(query, body);
        if (res.status === "ok") {
            load()
            toast.showToast("Notitie opgeslagen", "success", 3000);
        }
    }
    const handleNoteChange = (id_note, value) => {
        console.log(value, "note value", id_note)
        setEditedNotes(prev => ({
            ...prev,
            [id_note]: value
        }));
    };
    const handleKeyDown = async (e) => {
        if (e.key === "Enter") {
            e.stopPropagation();
            e.preventDefault();
            await handleSave();
        }
    };
    const handleKeyDownUpdate = async (e, id_note) => {
        if (e.key === "Enter") {
            e.stopPropagation();
            e.preventDefault();
            await handleUpdateNote(id_note);
        }
    };
    const handleUpdateNote = async (id_note) => {
        const query = "api/tasks/update/note";
        console.log(editedNotes[id_note], "edited note value", id_note)
        const body = {
            id_note,
            note: editedNotes[id_note]
        };
        const res = await updateAPI(query, body);
        if (res.status === "ok") {
            load();
            toast.showToast("Notitie bijgewerkt", "success", 3000);
        }
    };
    const removeNote = async (id_note) => {
        const query = "api/tasks/remove/note";
        const body = { id_note };
        const res = await deleteAPI(query, body);
        if (res.status === "ok") {
            load();
        }
    };
    useEffect(() => {
        setEditedNotes({
            ...notes.reduce((acc, note) => {
                acc[note.id_note] = note.note;
                return acc;
            }, {})
        });
    }, [notes])
    return (
        <VerticalLayout className="lead-detail-box" style={{ maxHeight: "47rem", width: "unset", minWidth: "clamp(10rem, 20vw ,20rem)", gap: ".5rem", height: "max-content" }}>
            <TitleAtom className={"medium-text"} style={{ width: "unset", }} title={"Notities"} />
            <HorizontalLayout style={{ gap: ".5rem" }}>
                <InputForm placeholder={"Notities toevoegen"} onKeyDown={handleKeyDown} typeInput={(v) => setNoteText(v)} style={{ margin: "0", width: "100%" }} />
                <StandardButton size="small" onClick={handleSave} style={{ margin: "0" }}>
                    {icon("plus", 20, "var(--white)")}
                </StandardButton>
            </HorizontalLayout>
            <GridLayout gap="1rem" columnsTemplate="repeat(auto-fit, minmax(200px, 1fr))" rowTemplate={"auto"} style={{ marginTop: ".5rem", width: "unset" }}>
                {notes.map(note => <TextAreaForm onKeyDown={(e) => handleKeyDownUpdate(e, note.id_note)} typeInput={(v) => handleNoteChange(note.id_note, v)} value={editedNotes[note.id_note] ?? note.note} style={{ margin: "0", width: "100%" }} />)}
            </GridLayout>
        </VerticalLayout>
    )
}

const Steps = () => {
    const steps = [
        { label: "Contact gelegd", completed: true },
        { label: "Behoefte vastgesteld", completed: true },
        { label: "Voorstel gedaan", completed: false },
        { label: "Onderhandeling", completed: false },
        { label: "Afgesloten", completed: false },
    ];
    return (
        <VerticalLayout className="phone-lead-step lead-detail-box" style={{ gap: ".1rem", maxHeight: "15rem", overflowY: "auto", borderLeft: "1px solid var(--border-color)", padding: ".5rem 1rem", width: "auto" }}>
            <TitleAtom className={"medium-text"} style={{ width: "unset", marginBottom: ".5rem" }} title={"Voortgang"} />
            {steps.map((step, index) => (
                <StepItem
                    key={index}
                    index={index}
                    label={step.label}
                    completed={step.completed}
                    isLast={index === steps.length - 1}
                />
            ))}
        </VerticalLayout>
    );
};

const StepItem = ({ index, label, completed, isLast }) => {
    const id_task = 1
    const navigationContext = useContext(PageContext);
    const handleGoToTasksInfo = () => {
        navigationContext.handleNavigation({ ...navigationContext.sidebar, navigation: 18, id_task: id_task });
        //need id_task
    }
    return (
        <HorizontalLayout className="step-container" onClick={handleGoToTasksInfo}>
            <VerticalLayout style={{ alignItems: "center", width: "unset" }}>
                <VerticalLayout className={`step-circle ${completed ? "completed" : "incomplete"}`}>
                    {index + 1}
                </VerticalLayout>
                {!isLast && (
                    <VerticalLayout className={`step-line ${completed ? "completed" : "incomplete"}`} />
                )}
            </VerticalLayout>
            <VerticalLayout className="text-container" style={{ gap: "0.1rem" }}>
                <TextAtom className={"xsmall-text semi-bold standard-text-color"} title={label} />
                <TextAtom className={`xsmall-text standard-text-color-light ${completed ? "completed" : "incomplete"}`} title={completed ? "Voltooid" : "In behandeling"} />
            </VerticalLayout>
        </HorizontalLayout>
    );
};


const LeadInfoTable = ({ lead, tasks, load, entityType }) => {
    const [taskModalActive, setTaskModalActive] = useState(false);
    const navigationContext = useContext(PageContext);
    const columns = [
        { key: "title", label: "Actienaam" },
        { key: "description", label: "Omschrijving" },
        { key: "status", label: "Status" },
        { key: "manual", label: "Verantwoordelijke" },
    ];
    const filters = [...[0, 1, 2, 3].map(statusId => ({ id: statusId, label: STATUS[statusId] || "not-found" }))];
    const rows = tasks.map(task => ({
        ...task,
        manual: task.manual === 0 ? "AI" : "Gebruiker",
        note: (task.note || "").trim() || "Geen notitie",
        contact: <>{task.email} <br /> {task.phone_nummer}</>,
    }));

    return (
        <>
            {entityType === "lead" ? <AddTaskToLead lead={lead} visible={taskModalActive} dismiss={() => setTaskModalActive(false)} load={load} /> : <AddTaskToCompany company={lead} visible={taskModalActive} dismiss={() => setTaskModalActive(false)} load={load} />}
            {tasks?.length > 0 ? <ReusableTable
                visible={tasks?.length > 0}
                title={<>Gekoppelde taken <span className="extra-count">({rows.length})</span></>}
                addLabel="Nieuwe lead-taak"
                onAdd={() => setTaskModalActive(!taskModalActive)}
                columns={columns}
                rows={rows}
                unitName="taken"
                itemsPerPage={4}
            /> : <FallbackTable unitName="taken" onClick={() => setTaskModalActive(!taskModalActive)} />}
        </>

    )

}
const FallbackTable = ({ unitName, onClick }) => {
    return (
        <HorizontalLayout className="card-background-color full-table-container standard-padding space-between" style={{ width: "auto" }}>
            <TitleAtom className={"medium-text"} title={`Gekoppelde ${unitName}`} />
            <StandardButton style={{ gap: ".5rem" }} className={"no-margin-left"} onClick={onClick}>
                {icon("plus", 20, "var(--white)")}
            </StandardButton>
        </HorizontalLayout>
    )
}


const DeepSearchButton = ({ handleDeepSearch }) => {
    return (
        <StandardButton onClick={handleDeepSearch} style={{ gap: ".5rem", marginLeft: "auto" }}>
            {icon("robot", 20, "var(--white)")}
            {/* <TextAtom style={{ fontWeight: "bold" }} title={"Diepgaand onderzoek"} /> */}
        </StandardButton>
    )
}